import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, Phone, MapPin, CheckCircle2, Clock, X, Loader2, Search, Filter, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-sky-100 text-sky-700",
  done: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-gray-200 text-gray-500",
};
const TYPE_COLORS = {
  site_visit: "bg-violet-100 text-violet-700",
  appointment: "bg-blue-100 text-blue-700",
  demo_class: "bg-indigo-100 text-indigo-700",
  callback: "bg-amber-100 text-amber-700",
  brochure_request: "bg-fuchsia-100 text-fuchsia-700",
  consultation: "bg-rose-100 text-rose-700",
  table_booking: "bg-yellow-100 text-yellow-700",
  product_enquiry: "bg-cyan-100 text-cyan-700",
  order_request: "bg-emerald-100 text-emerald-700",
};

export default function BusinessActions() {
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_status: {}, by_type: {}, by_category: {} });
  const [filters, setFilters] = useState({ status: "", action_type: "", category: "", q: "" });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.action_type) params.set("action_type", filters.action_type);
      if (filters.category) params.set("category", filters.category);
      if (filters.q) params.set("q", filters.q);
      const [listR, sumR] = await Promise.all([
        fetch(`${API}/business-actions?${params.toString()}`, { headers }).then(r => r.ok ? r.json() : { items: [] }),
        fetch(`${API}/business-actions/summary`, { headers }).then(r => r.ok ? r.json() : null),
      ]);
      setItems(listR.items || []);
      setSummary(sumR || { total: 0, by_status: {}, by_type: {}, by_category: {} });
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [headers, filters]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    const r = await fetch(`${API}/business-actions/${id}`, {
      method: "PATCH", headers,
      body: JSON.stringify({ status }),
    });
    if (r.ok) load();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50/30 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <header data-testid="ba-header">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 inline-flex items-center gap-2">
            <Inbox className="w-6 h-6 text-sky-600" /> Business Actions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Every site visit / appointment / demo / callback / order request the AI auto-creates from WhatsApp lands here.
          </p>
        </header>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ba-stats">
          <Stat label="Total" value={summary.total} accent="from-slate-500 to-slate-700" />
          <Stat label="Pending" value={summary.by_status?.pending || 0} accent="from-amber-400 to-amber-600" />
          <Stat label="Confirmed" value={summary.by_status?.confirmed || 0} accent="from-sky-500 to-sky-700" />
          <Stat label="Done" value={summary.by_status?.done || 0} accent="from-emerald-500 to-emerald-700" />
        </div>

        {/* Filters */}
        <div className="grid sm:grid-cols-5 gap-3" data-testid="ba-filters">
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm" data-testid="ba-filter-status">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="done">Done</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select value={filters.action_type} onChange={(e) => setFilters({ ...filters, action_type: e.target.value })}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm" data-testid="ba-filter-type">
            <option value="">All types</option>
            {Object.keys(TYPE_COLORS).map(t => (
              <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
            ))}
          </select>
          <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm" data-testid="ba-filter-category">
            <option value="">All categories</option>
            {Object.keys(summary.by_category || {}).map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <div className="sm:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search by customer, phone, entity, notes"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
              data-testid="ba-filter-q"
            />
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="ba-list">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-gray-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Filter className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm">No actions match your filters.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map(it => (
                <li
                  key={it.id}
                  className="px-4 py-3 hover:bg-sky-50/40 cursor-pointer flex flex-col sm:flex-row sm:items-center gap-3"
                  onClick={() => setActive(it)}
                  data-testid={`ba-row-${it.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide ${TYPE_COLORS[it.action_type] || "bg-slate-100 text-slate-600"}`}>
                        {it.action_type.replace(/_/g, " ")}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${STATUS_COLORS[it.status] || "bg-slate-100 text-slate-600"}`}>
                        {it.status}
                      </span>
                      {it.entity_name && (
                        <span className="text-[11px] text-gray-500">· {it.entity_name}</span>
                      )}
                    </div>
                    <p className="font-semibold text-gray-900 text-sm mt-0.5 truncate">
                      {it.customer_name} <span className="text-gray-400 font-normal">· {it.customer_phone}</span>
                    </p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {it.preferred_date}
                      {it.time_window || it.preferred_time ? ` · ${it.time_window || it.preferred_time}` : ""}
                      {it.pickup_required ? ` · pickup: ${it.pickup_location || "TBD"}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {it.status === "pending" && (
                      <button onClick={(e) => { e.stopPropagation(); setStatus(it.id, "confirmed"); }}
                        className="text-xs px-2.5 py-1 rounded-md bg-sky-600 text-white hover:bg-sky-700"
                        data-testid={`ba-confirm-${it.id}`}>
                        Confirm
                      </button>
                    )}
                    {it.status !== "done" && it.status !== "cancelled" && (
                      <button onClick={(e) => { e.stopPropagation(); setStatus(it.id, "done"); }}
                        className="text-xs px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid={`ba-done-${it.id}`}>
                        Mark done
                      </button>
                    )}
                    {it.status !== "cancelled" && it.status !== "done" && (
                      <button onClick={(e) => { e.stopPropagation(); setStatus(it.id, "cancelled"); }}
                        className="text-xs px-2.5 py-1 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50"
                        data-testid={`ba-cancel-${it.id}`}>
                        Cancel
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {active && (
        <DetailDrawer item={active} onClose={() => setActive(null)} setStatus={setStatus} />
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`text-2xl font-bold mt-0.5 bg-gradient-to-r ${accent} bg-clip-text text-transparent`}>{value}</p>
    </div>
  );
}

function DetailDrawer({ item, onClose, setStatus }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="ba-drawer">
      <aside onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-white shadow-2xl h-full overflow-y-auto">
        <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">{item.action_type.replace(/_/g, " ")}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <Row icon={CheckCircle2} label="Status">
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[item.status]}`}>{item.status}</span>
          </Row>
          <Row icon={Calendar} label="When">
            {item.preferred_date}{(item.time_window || item.preferred_time) ? ` · ${item.time_window || item.preferred_time}` : ""}
          </Row>
          <Row icon={Phone} label="Customer">
            {item.customer_name} · {item.customer_phone}
          </Row>
          {item.entity_name && (
            <Row icon={Inbox} label="Entity">
              {item.entity_name} <span className="text-gray-400 text-xs">({item.entity_type || "—"})</span>
            </Row>
          )}
          {item.pickup_required && (
            <Row icon={MapPin} label="Pickup">{item.pickup_location || "—"}</Row>
          )}
          {item.notes && (
            <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-xs text-gray-600 whitespace-pre-wrap">
              <p className="font-semibold uppercase tracking-wide text-[10px] text-gray-400 mb-1">Notes</p>
              {item.notes}
            </div>
          )}
          <Row icon={Clock} label="Created">
            {new Date(item.created_at).toLocaleString()}
          </Row>
          <div className="flex gap-2 pt-2">
            {item.status === "pending" && (
              <button onClick={() => setStatus(item.id, "confirmed")}
                className="flex-1 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700"
                data-testid="ba-drawer-confirm">Confirm</button>
            )}
            {item.status !== "done" && item.status !== "cancelled" && (
              <button onClick={() => setStatus(item.id, "done")}
                className="flex-1 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
                data-testid="ba-drawer-done">Mark done</button>
            )}
            {item.status !== "cancelled" && item.status !== "done" && (
              <button onClick={() => setStatus(item.id, "cancelled")}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50"
                data-testid="ba-drawer-cancel">Cancel</button>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
        <p className="text-sm text-gray-800">{children}</p>
      </div>
    </div>
  );
}
